import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaRocket, FaSearch, FaNetworkWired, FaBook, FaUsers, FaLightbulb } from 'react-icons/fa';

const HomePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="flex justify-center min-h-screen">
      <div className="w-full px-8 py-12">
        {/* Hero Section */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Welcome to PSpace
            </h1>
            <p className="text-xl text-gray-700 mb-4 leading-relaxed">
              Your gateway to AI Planning Software
            </p>
            <p className="text-gray-600 mb-8">
              Discover, explore, and understand advanced planning tools available today
            </p>
            
            {/* CTA Buttons */}
            <div className="flex gap-4 justify-center flex-wrap">
              <Link
                to="/discover"
                className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg transition-all duration-300 hover:bg-green-700 hover:scale-105 hover:shadow-lg font-semibold"
              >
                <FaSearch className="mr-2" />
                Discover Tools
              </Link>
              <Link
                to="/knowledge-graph"
                className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg transition-all duration-300 hover:bg-green-700 hover:scale-105 hover:shadow-lg font-semibold"
              >
                <FaNetworkWired className="mr-2" />
                Knowledge Graph
              </Link>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900">
            What You Can Do
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {/* Card 1 - Discover */}
            <div className="bg-white/10 p-6 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-green-200/20">
              <div className="flex items-center mb-4">
                <FaSearch className="text-3xl text-green-600 mr-3" />
                <h3 className="text-2xl font-semibold text-gray-900">Discover</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Browse our extensive collection of AI planning tools and software. Search, filter, and compare different planning systems based on your needs.
              </p>
            </div>

            {/* Card 2 - Knowledge Graph */}
            <div className="bg-white/10 p-6 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-green-200/20">
              <div className="flex items-center mb-4">
                <FaNetworkWired className="text-3xl text-green-600 mr-3" />
                <h3 className="text-2xl font-semibold text-gray-900">Knowledge Graph</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Visualize relationships between planning tools, algorithms, and their implementations. Understand the connections in the AI planning ecosystem.
              </p>
            </div>

            {/* Card 3 - Learn */}
            <div className="bg-white/10 p-6 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-green-200/20">
              <div className="flex items-center mb-4">
                <FaBook className="text-3xl text-green-600 mr-3" />
                <h3 className="text-2xl font-semibold text-gray-900">Learn</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Access research papers, lecture notes, and other resources to deepen your knowledge and understanding of AI planning and related technologies.
              </p>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="mb-16">
          <div className="flex items-center mb-4">
            <FaRocket className="text-2xl text-green-600 mr-3" />
            <h2 className="text-3xl font-bold text-gray-900">About PSpace</h2>
          </div>
          <p className="text-gray-700 mb-6 leading-relaxed">
            At PlanX Universe, we are a team of AI enthusiasts, researchers, and developers passionate about advancing the field of automated planning. PSpace is an open repository bridging the gap between planning practitioners, software developers, and the research community.
          </p>
          <p className="text-gray-700 mb-6 leading-relaxed">
            Whether you're a researcher, developer, or enthusiast, PSpace provides you with comprehensive information about AI planning tools, their capabilities, implementation details, and the relationships between them.
          </p>
        </section>

        {/* Why It Matters Section */}
        <section className="mb-16">
          <div className="flex items-center mb-4">
            <FaLightbulb className="text-2xl text-green-600 mr-3" />
            <h2 className="text-3xl font-bold text-gray-900">Why AI Planning Matters</h2>
          </div>
          <p className="text-gray-700 mb-4 leading-relaxed">
            The field of automated planning plays a critical role in:
          </p>
          <ul className="list-disc pl-8 text-gray-700 space-y-2 mb-6">
            <li>Robotics and autonomous systems</li>
            <li>Logistics and supply chain optimization</li>
            <li>Smart buildings and IoT</li>
            <li>Autonomous driving</li>
            <li>Cloud computing resource allocation</li>
            <li>Game AI and interactive entertainment</li>
          </ul>
          <p className="text-gray-700 leading-relaxed">
            Yet, valuable tools often remain siloed or underused. PSpace democratizes access to these innovations.
          </p>
        </section>

        {/* Get Involved Section */}
        {/* <section className="bg-white/10 p-8 rounded-2xl border border-green-200/20 text-center">
          <div className="flex items-center justify-center mb-4">
            <FaUsers className="text-2xl text-green-600 mr-3" />
            <h2 className="text-3xl font-bold text-gray-900">Get Involved</h2>
          </div>
          <p className="text-gray-700 mb-6 leading-relaxed">
            Want to contribute a planner? Curious to try one out? We welcome developers, researchers, and industry practitioners alike.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link 
              to="/add-tool" 
              className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg transition-all duration-300 hover:bg-green-700 hover:scale-105 hover:shadow-lg font-semibold"
            >
              <FaRocket className="mr-2" />
              Submit a Tool
            </Link>
            <a 
              href="mailto:planx.universe@gmail.com" 
              className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg transition-all duration-300 hover:bg-green-700 hover:scale-105 hover:shadow-lg font-semibold"
            >
              Contact Us
            </a>
          </div>
        </section> */}
      </div>
    </div>
  );
};

export default HomePage;
